import React, { useEffect, useMemo } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import Input from '../Form/Input';
import Select from '../Form/Select';
import TextArea from '../Form/TextArea';
import Button from '../Form/Button';
import { CrewEvaluation } from '../../types';
import { useEmployees } from '../../hooks/useEmployees';
import { useBranches } from '../../hooks/useBranches';
import { EvaluationCategoriesSection, defaultCategories } from './CrewEvaluationFormSections';

interface CrewEvaluationFormProps {
  evaluation?: CrewEvaluation;
  onSave: (data: Partial<CrewEvaluation>) => void;
  onCancel: () => void;
  evaluatorRole: 'vessel_manager' | 'chief_manager';
  evaluatorName: string;
  evaluatorId: string;
}

const CrewEvaluationForm: React.FC<CrewEvaluationFormProps> = ({
  evaluation,
  onSave,
  onCancel,
  evaluatorRole,
  evaluatorName,
  evaluatorId
}) => {
  const { data: apiEmployees = [] } = useEmployees({ populate: '*' });
  const { data: apiBranches = [] } = useBranches({ populate: '*' });
  
  const vessels = useMemo(() => {
    return apiBranches.filter((b: any) => b.type === 'vessel');
  }, [apiBranches]);

  const employeeOptions = useMemo(() => {
    if (!apiEmployees || apiEmployees.length === 0) return [];
    return apiEmployees.map((emp: any) => ({
      value: emp.documentId || emp.id?.toString() || '',
      label: `${emp.name || 'Unknown'} - ${emp.rank || emp.position || 'Employee'}`
    })).filter((opt: any) => opt.value);
  }, [apiEmployees]);

  const vesselOptions = useMemo(() => {
    if (!vessels || vessels.length === 0) return [];
    return vessels.map((v: any) => ({
      value: v.documentId || v.id?.toString() || '',
      label: v.name || 'Unknown Vessel'
    })).filter((opt: any) => opt.value);
  }, [vessels]);

  const { register, handleSubmit, control, watch, setValue, formState: { errors } } = useForm<CrewEvaluation>({
    defaultValues: evaluation || {
      id: '',
      employeeId: '',
      employeeName: '',
      evaluatorId,
      evaluatorName,
      evaluatorRole,
      vesselId: '',
      vesselName: '',
      evaluationPeriod: { fromDate: '', toDate: '' },
      categories: defaultCategories,
      overallRating: 0,
      overallComments: '',
      status: 'draft',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    }
  });

  const { fields: categoryFields, update: updateCategory } = useFieldArray({
    control,
    name: 'categories'
  });

  const watchedCategories = watch('categories');
  const selectedEmployeeId = watch('employeeId');
  const selectedVesselId = watch('vesselId');

  useEffect(() => {
    const selectedEmployee = apiEmployees.find((emp: any) => 
      emp.documentId === selectedEmployeeId || emp.id?.toString() === selectedEmployeeId
    );
    if (selectedEmployee) {
      setValue('employeeName', selectedEmployee.name || '');
    }
  }, [selectedEmployeeId, setValue, apiEmployees]);

  useEffect(() => {
    const selectedVessel = vessels.find((vessel: any) => 
      vessel.documentId === selectedVesselId || vessel.id?.toString() === selectedVesselId
    );
    if (selectedVessel) {
      setValue('vesselName', selectedVessel.name || '');
    }
  }, [selectedVesselId, setValue, vessels]);

  useEffect(() => {
    watchedCategories?.forEach((category, categoryIndex) => {
      const totalRating = category.items.reduce((sum, item) => sum + item.rating, 0);
      const averageRating = category.items.length > 0 ? totalRating / category.items.length : 0;
      
      if (category.categoryRating !== averageRating) {
        updateCategory(categoryIndex, {
          ...category,
          categoryRating: parseFloat(averageRating.toFixed(1))
        });
      }
    });
  }, [watchedCategories, updateCategory]);

  useEffect(() => {
    if (watchedCategories && watchedCategories.length > 0) {
      const totalCategoryRating = watchedCategories.reduce((sum, category) => sum + category.categoryRating, 0);
      const overallRating = parseFloat((totalCategoryRating / watchedCategories.length).toFixed(1));
      setValue('overallRating', overallRating);
    }
  }, [watchedCategories, setValue]);

  const updateItemRating = (categoryIndex: number, itemIndex: number, rating: number) => {
    const updatedCategory = { ...watchedCategories[categoryIndex] };
    updatedCategory.items[itemIndex].rating = rating;
    updateCategory(categoryIndex, updatedCategory);
  };

  const updateItemComments = (categoryIndex: number, itemIndex: number, comments: string) => {
    const updatedCategory = { ...watchedCategories[categoryIndex] };
    updatedCategory.items[itemIndex].comments = comments;
    updateCategory(categoryIndex, updatedCategory);
  };

  const onSubmit = (data: CrewEvaluation) => {
    onSave({ ...data, updatedAt: new Date().toISOString(), status: 'submitted' as const });
  };

  const onSaveDraft = (data: CrewEvaluation) => {
    onSave({ ...data, updatedAt: new Date().toISOString(), status: 'draft' as const });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[95vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {evaluation ? 'Edit Crew Evaluation' : 'New Crew Evaluation'}
          </h2>
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600 focus:outline-none"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form className="p-6">
          {/* Basic Information */}
          <div className="mb-8">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <Select
                label="Employee"
                {...register('employeeId', { required: 'Employee is required' })}
                options={employeeOptions}
                error={errors.employeeId?.message}
              />
              {evaluatorRole === 'vessel_manager' && (
                <Select
                  label="Vessel"
                  {...register('vesselId', { required: 'Vessel is required for vessel managers' })}
                  options={vesselOptions}
                  error={errors.vesselId?.message}
                />
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                type="date"
                label="Evaluation Period - From"
                {...register('evaluationPeriod.fromDate', { required: 'From date is required' })}
                error={errors.evaluationPeriod?.fromDate?.message}
              />
              <Input
                type="date"
                label="Evaluation Period - To"
                {...register('evaluationPeriod.toDate', { required: 'To date is required' })}
                error={errors.evaluationPeriod?.toDate?.message}
              />
            </div>
          </div>

          <EvaluationCategoriesSection
            categoryFields={categoryFields}
            watchedCategories={watchedCategories}
            updateItemRating={updateItemRating}
            updateItemComments={updateItemComments}
          />

          {/* Overall Assessment */}
          <div className="mb-8">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Overall Assessment</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Overall Rating (Calculated)
                </label>
                <div className="w-20 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <span className="text-lg font-bold text-green-800">
                    {watch('overallRating') || 0}
                  </span>
                </div>
              </div>
            </div>
            <TextArea
              label="Overall Comments"
              {...register('overallComments')}
              rows={4}
              placeholder="Provide overall assessment and recommendations..."
            />
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
            <Button type="button" variant="secondary" onClick={onCancel}>
              Cancel
            </Button>
            <Button type="button" variant="secondary" onClick={handleSubmit(onSaveDraft)}>
              Save as Draft
            </Button>
            <Button type="button" variant="primary" onClick={handleSubmit(onSubmit)}>
              Submit for Approval
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CrewEvaluationForm;
